import React, { useState, useEffect } from 'react';
import {
    Settings,
    Palette,
    Bell,
    Link2,
    Shield,
    Save,
    Clock,
    Calendar,
    Globe,
    Sun,
    Moon,
    Monitor,
    Mail,
    Smartphone,
} from 'lucide-react';
import { useSettingsStore } from '../../store/settingsStore';

type SettingsTab = 'general' | 'branding' | 'notifications' | 'integrations' | 'security';

const SettingsPage: React.FC = () => {
    const { settings, isLoading, fetchSettings, updateSettings } = useSettingsStore();
    const [activeTab, setActiveTab] = useState<SettingsTab>('general');
    const [isSaving, setIsSaving] = useState(false);

    // Form state for each category
    const [generalForm, setGeneralForm] = useState({
        teamName: '',
        timezone: 'UTC',
        dateFormat: 'YYYY-MM-DD',
        timeFormat: '12h',
        weekStartsOn: 1,
    });

    const [brandingForm, setBrandingForm] = useState({
        primaryColor: '#6366f1',
        accentColor: '#8b5cf6',
        darkMode: 'auto',
        emailSignature: '',
        invoiceFooter: '',
    });

    const [notificationsForm, setNotificationsForm] = useState({
        emailNotifications: true,
        pushNotifications: true,
        digestFrequency: 'instant',
        quietHoursStart: '',
        quietHoursEnd: '',
        partnershipUpdates: true,
        invoiceReminders: true,
        contractAlerts: true,
        projectDeadlines: true,
        paymentReceived: true,
        reviewRequests: true,
    });

    const [securityForm, setSecurityForm] = useState({
        twoFactorRequired: false,
        sessionTimeout: 60,
        minPasswordLength: 8,
        requireUppercase: true,
        requireNumbers: true,
    });

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    useEffect(() => {
        if (settings) {
            setGeneralForm({
                teamName: settings.general?.teamName || '',
                timezone: settings.general?.timezone || 'UTC',
                dateFormat: settings.general?.dateFormat || 'YYYY-MM-DD',
                timeFormat: settings.general?.timeFormat || '12h',
                weekStartsOn: settings.general?.weekStartsOn || 1,
            });
            setBrandingForm({
                primaryColor: settings.branding?.primaryColor || '#6366f1',
                accentColor: settings.branding?.accentColor || '#8b5cf6',
                darkMode: settings.branding?.darkMode || 'auto',
                emailSignature: settings.branding?.emailSignature || '',
                invoiceFooter: settings.branding?.invoiceFooter || '',
            });
            setNotificationsForm({
                emailNotifications: settings.notifications?.emailNotifications ?? true,
                pushNotifications: settings.notifications?.pushNotifications ?? true,
                digestFrequency: settings.notifications?.digestFrequency || 'instant',
                quietHoursStart: settings.notifications?.quietHoursStart || '',
                quietHoursEnd: settings.notifications?.quietHoursEnd || '',
                partnershipUpdates: settings.notifications?.partnershipUpdates ?? true,
                invoiceReminders: settings.notifications?.invoiceReminders ?? true,
                contractAlerts: settings.notifications?.contractAlerts ?? true,
                projectDeadlines: settings.notifications?.projectDeadlines ?? true,
                paymentReceived: settings.notifications?.paymentReceived ?? true,
                reviewRequests: settings.notifications?.reviewRequests ?? true,
            });
            setSecurityForm({
                twoFactorRequired: settings.security?.twoFactorRequired || false,
                sessionTimeout: settings.security?.sessionTimeout || 60,
                minPasswordLength: settings.security?.minPasswordLength || 8,
                requireUppercase: settings.security?.requireUppercase ?? true,
                requireNumbers: settings.security?.requireNumbers ?? true,
            });
        }
    }, [settings]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const updateData: any = {};

            if (activeTab === 'general') {
                updateData.general = generalForm;
            } else if (activeTab === 'branding') {
                updateData.branding = brandingForm;
            } else if (activeTab === 'notifications') {
                updateData.notifications = notificationsForm;
            } else if (activeTab === 'security') {
                updateData.security = securityForm;
            }

            await updateSettings(updateData);
        } catch (error) {
            console.error('Failed to save:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const tabs = [
        { id: 'general' as const, label: 'General', icon: Settings },
        { id: 'branding' as const, label: 'Branding', icon: Palette },
        { id: 'notifications' as const, label: 'Notifications', icon: Bell },
        { id: 'integrations' as const, label: 'Integrations', icon: Link2 },
        { id: 'security' as const, label: 'Security', icon: Shield },
    ];

    const timezones = ['UTC', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Tokyo', 'Asia/Kolkata', 'Australia/Sydney'];
    const dateFormats = ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY'];

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your team preferences and configuration</p>
            </div>

            <div className="flex gap-6">
                {/* Sidebar Navigation */}
                <div className="w-64 flex-shrink-0">
                    <nav className="bg-white dark:bg-gray-800 rounded-2xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-2">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === tab.id
                                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                    }`}
                            >
                                <tab.icon className="w-5 h-5" />
                                <span className="font-medium">{tab.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Content Area */}
                <div className="flex-1">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                        {/* General Settings */}
                        {activeTab === 'general' && (
                            <div className="space-y-6">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                    <Settings className="w-5 h-5 text-primary-500" />
                                    General Settings
                                </h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Team Name
                                        </label>
                                        <input
                                            type="text"
                                            value={generalForm.teamName}
                                            onChange={(e) => setGeneralForm({ ...generalForm, teamName: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            <Globe className="w-4 h-4 inline mr-1" />
                                            Timezone
                                        </label>
                                        <select
                                            value={generalForm.timezone}
                                            onChange={(e) => setGeneralForm({ ...generalForm, timezone: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                        >
                                            {timezones.map((tz) => (
                                                <option key={tz} value={tz}>{tz}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            <Calendar className="w-4 h-4 inline mr-1" />
                                            Date Format
                                        </label>
                                        <select
                                            value={generalForm.dateFormat}
                                            onChange={(e) => setGeneralForm({ ...generalForm, dateFormat: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                        >
                                            {dateFormats.map((df) => (
                                                <option key={df} value={df}>{df}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            <Clock className="w-4 h-4 inline mr-1" />
                                            Time Format
                                        </label>
                                        <div className="flex gap-4">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    checked={generalForm.timeFormat === '12h'}
                                                    onChange={() => setGeneralForm({ ...generalForm, timeFormat: '12h' })}
                                                    className="text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-gray-700 dark:text-gray-300">12-hour</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    checked={generalForm.timeFormat === '24h'}
                                                    onChange={() => setGeneralForm({ ...generalForm, timeFormat: '24h' })}
                                                    className="text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-gray-700 dark:text-gray-300">24-hour</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Week Starts On
                                        </label>
                                        <select
                                            value={generalForm.weekStartsOn}
                                            onChange={(e) => setGeneralForm({ ...generalForm, weekStartsOn: parseInt(e.target.value) })}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                        >
                                            <option value={0}>Sunday</option>
                                            <option value={1}>Monday</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Branding Settings */}
                        {activeTab === 'branding' && (
                            <div className="space-y-6">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                    <Palette className="w-5 h-5 text-primary-500" />
                                    Branding Settings
                                </h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Primary Color
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="color"
                                                value={brandingForm.primaryColor}
                                                onChange={(e) => setBrandingForm({ ...brandingForm, primaryColor: e.target.value })}
                                                className="w-12 h-12 rounded-lg border-2 border-gray-200 dark:border-gray-600 cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                value={brandingForm.primaryColor}
                                                onChange={(e) => setBrandingForm({ ...brandingForm, primaryColor: e.target.value })}
                                                className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Accent Color
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="color"
                                                value={brandingForm.accentColor}
                                                onChange={(e) => setBrandingForm({ ...brandingForm, accentColor: e.target.value })}
                                                className="w-12 h-12 rounded-lg border-2 border-gray-200 dark:border-gray-600 cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                value={brandingForm.accentColor}
                                                onChange={(e) => setBrandingForm({ ...brandingForm, accentColor: e.target.value })}
                                                className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white"
                                            />
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Theme Mode
                                        </label>
                                        <div className="flex gap-4">
                                            {[
                                                { value: 'light', label: 'Light', icon: Sun },
                                                { value: 'dark', label: 'Dark', icon: Moon },
                                                { value: 'auto', label: 'System', icon: Monitor },
                                            ].map((mode) => (
                                                <button
                                                    key={mode.value}
                                                    onClick={() => setBrandingForm({ ...brandingForm, darkMode: mode.value })}
                                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all ${brandingForm.darkMode === mode.value
                                                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                                                        : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                                                        }`}
                                                >
                                                    <mode.icon className="w-4 h-4" />
                                                    {mode.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Email Signature
                                        </label>
                                        <textarea
                                            value={brandingForm.emailSignature}
                                            onChange={(e) => setBrandingForm({ ...brandingForm, emailSignature: e.target.value })}
                                            rows={3}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                                            placeholder="Your default email signature..."
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Invoice Footer
                                        </label>
                                        <textarea
                                            value={brandingForm.invoiceFooter}
                                            onChange={(e) => setBrandingForm({ ...brandingForm, invoiceFooter: e.target.value })}
                                            rows={2}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                                            placeholder="Thank you for your business!"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Notification Settings */}
                        {activeTab === 'notifications' && (
                            <div className="space-y-6">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                    <Bell className="w-5 h-5 text-primary-500" />
                                    Notification Preferences
                                </h2>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <div className="flex items-center gap-3">
                                            <Mail className="w-5 h-5 text-gray-500" />
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">Email Notifications</p>
                                                <p className="text-sm text-gray-500">Receive updates via email</p>
                                            </div>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={notificationsForm.emailNotifications}
                                                onChange={(e) => setNotificationsForm({ ...notificationsForm, emailNotifications: e.target.checked })}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        </label>
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <div className="flex items-center gap-3">
                                            <Smartphone className="w-5 h-5 text-gray-500" />
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">Push Notifications</p>
                                                <p className="text-sm text-gray-500">Receive push notifications</p>
                                            </div>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={notificationsForm.pushNotifications}
                                                onChange={(e) => setNotificationsForm({ ...notificationsForm, pushNotifications: e.target.checked })}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        </label>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mt-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Digest Frequency
                                            </label>
                                            <select
                                                value={notificationsForm.digestFrequency}
                                                onChange={(e) => setNotificationsForm({ ...notificationsForm, digestFrequency: e.target.value })}
                                                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white"
                                            >
                                                <option value="instant">Instant</option>
                                                <option value="daily">Daily Digest</option>
                                                <option value="weekly">Weekly Digest</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="mt-6">
                                        <h3 className="font-medium text-gray-900 dark:text-white mb-4">Event Notifications</h3>
                                        <div className="grid grid-cols-2 gap-3">
                                            {[
                                                { key: 'partnershipUpdates', label: 'Partnership Updates' },
                                                { key: 'invoiceReminders', label: 'Invoice Reminders' },
                                                { key: 'contractAlerts', label: 'Contract Alerts' },
                                                { key: 'projectDeadlines', label: 'Project Deadlines' },
                                                { key: 'paymentReceived', label: 'Payment Received' },
                                                { key: 'reviewRequests', label: 'Review Requests' },
                                            ].map((item) => (
                                                <label key={item.key} className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={notificationsForm[item.key as keyof typeof notificationsForm] as boolean}
                                                        onChange={(e) => setNotificationsForm({ ...notificationsForm, [item.key]: e.target.checked })}
                                                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                                                    />
                                                    <span className="text-gray-700 dark:text-gray-300">{item.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Integrations */}
                        {activeTab === 'integrations' && (
                            <div className="space-y-6">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                    <Link2 className="w-5 h-5 text-primary-500" />
                                    Integrations
                                </h2>

                                <div className="space-y-4">
                                    {[
                                        { name: 'Google Calendar', desc: 'Sync events with Google Calendar', connected: false },
                                        { name: 'Slack', desc: 'Get notifications in Slack', connected: false },
                                        { name: 'Stripe', desc: 'Process payments with Stripe', connected: false },
                                        { name: 'PayPal', desc: 'Accept PayPal payments', connected: false },
                                        { name: 'Zapier', desc: 'Connect with 5000+ apps', connected: false },
                                    ].map((integration) => (
                                        <div key={integration.name} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">{integration.name}</p>
                                                <p className="text-sm text-gray-500">{integration.desc}</p>
                                            </div>
                                            <button className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                                                Connect
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Security Settings */}
                        {activeTab === 'security' && (
                            <div className="space-y-6">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                    <Shield className="w-5 h-5 text-primary-500" />
                                    Security Settings
                                </h2>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">Require Two-Factor Authentication</p>
                                            <p className="text-sm text-gray-500">Require all team members to use 2FA</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={securityForm.twoFactorRequired}
                                                onChange={(e) => setSecurityForm({ ...securityForm, twoFactorRequired: e.target.checked })}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        </label>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Session Timeout (minutes)
                                            </label>
                                            <input
                                                type="number"
                                                value={securityForm.sessionTimeout}
                                                onChange={(e) => setSecurityForm({ ...securityForm, sessionTimeout: parseInt(e.target.value) || 60 })}
                                                min={5}
                                                max={1440}
                                                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Min Password Length
                                            </label>
                                            <input
                                                type="number"
                                                value={securityForm.minPasswordLength}
                                                onChange={(e) => setSecurityForm({ ...securityForm, minPasswordLength: parseInt(e.target.value) || 8 })}
                                                min={6}
                                                max={32}
                                                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-6 mt-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={securityForm.requireUppercase}
                                                onChange={(e) => setSecurityForm({ ...securityForm, requireUppercase: e.target.checked })}
                                                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                                            />
                                            <span className="text-gray-700 dark:text-gray-300">Require uppercase letters</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={securityForm.requireNumbers}
                                                onChange={(e) => setSecurityForm({ ...securityForm, requireNumbers: e.target.checked })}
                                                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                                            />
                                            <span className="text-gray-700 dark:text-gray-300">Require numbers</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Save Button */}
                        {activeTab !== 'integrations' && (
                            <div className="mt-8 flex justify-end">
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
                                >
                                    {isSaving ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
